import { AfterViewInit, Component, ElementRef, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core'

import { ResponsiveComponent, SafeReactWrapper, VersionComponent } from '@nx-superprep/shared'
import { HeaderTemplateType, StudyService, UserService } from '@nx-superprep/backend/firebase'
import { AvatarComponent } from "../../avatar/avatar.component"
import { takeUntil } from 'rxjs';
import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common'
import { JoinClassroomState } from '../../join-classroom/join-classroom.component'
import { environment } from '@nx-superprep/environment'
import { BsDropdownModule } from 'ngx-bootstrap/dropdown'
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal'
import { TourMatMenuModule } from 'ngx-ui-tour-md-menu'
import { MatIconModule } from '@angular/material/icon'
import { MatMenuModule } from '@angular/material/menu'
import { MatButtonModule } from '@angular/material/button'
import { AuthComponent } from '../../auth'
import log from 'loglevel-es'

// React imports
import * as React from 'react'
import { Root, createRoot } from 'react-dom/client'
import { PopStudyNavBar } from '../../pop-study-nav-bar'

const containerElementRef = 'container'
const elementRef = 'react'

type HeaderActionType = HeaderTemplateType | 'home' | 'join' | 'profile' | 'logout' | 'popstudy' | 'popquiz' | 'popteams' | 'popstack' | 'feedback';

@Component({
  standalone: true,
  selector: 'pop-ui-headerlte',
  templateUrl: './headerlte.component.html',
  styleUrls: ['./headerlte.component.scss'],
  queries: {
    "confirmLogout": new ViewChild("_confirmLogout"),
  },
  imports: [
    NgClass, NgStyle, NgTemplateOutlet,
    AvatarComponent,
    VersionComponent,
    BsDropdownModule,
    MatButtonModule,
    MatMenuModule,
    MatIconModule,
    TourMatMenuModule
  ],
  providers:[BsModalService]
})
export class HeaderlteComponent extends ResponsiveComponent implements OnInit, AfterViewInit, OnChanges {
  isReact = true

  @ViewChild(containerElementRef, { static: true }) containerRef: ElementRef<Element> | undefined
  @ViewChild(elementRef, { static: true }) reactRef: ElementRef<Element> | undefined

  private root: Root | null = null

  classroomState: {status?: string} = {status: undefined}
  profileOpen = false

  public confirmLogout!: TemplateRef<unknown>
  private _bsModalRef!: BsModalRef

  private _menu = false
  @Input()
  get menu() { return this._menu }
  set menu(value) { this._menu = value; this.menuChange.emit(value) }
  @Output() menuChange = new EventEmitter<boolean>()

  @Input() activeTab: HeaderActionType = 'home'
  @Output() activeTabChange = new EventEmitter<HeaderActionType>()

  @Input() classroom: JoinClassroomState = {status:undefined}
  @Output() classroomChange = new EventEmitter<JoinClassroomState>()

  @Input() profile = false
  @Output() profileChange = new EventEmitter<boolean>()

  @Input() logo = { src: '/assets/icons/popstudy.svg', alt: 'popstudy-logo', width: 156, height: 30 }
  @Input() vector = { src: '/assets/icons/vector.svg', alt: 'vector' }
  @Input() avatar = { src: '/assets/images/avatar-ellipse.png', alt: 'Avatar-ellipse' }

  isCollapsed = true
  profileName = 'Guest'
  photoURL: string|null|undefined = undefined

  get isDevelopment() { return !environment.config?.isProduction }
  get isDebugMode() { return this.userService.isDebugMode }
  get isContentManager() { return this.userService.isContentManager }

  get isJoinActive() {
    return this.classroom.status === 'join'
  }

  get entries(): HeaderTemplateType[] {
    return this.userService.headerTemplateValues
  }

  cancel() {
    this._bsModalRef?.hide()
  }
  create() {
    this._bsModalRef?.hide()
    this._bsModalRef = this.modalService.show(AuthComponent, {animated: true, class: 'modal-md', initialState: {options: {state:'register'}}})
  }
  signout() {
    this._bsModalRef?.hide()
    this.userService.signOut()
  }
  confirm() {
    this._bsModalRef?.hide()
    this._bsModalRef = this.modalService.show(this.confirmLogout, {animated:true, class:'modal-md signout-modal'})
  }

  onSelect(
    item?: HeaderActionType,
    finalRoute?: string
  ) {
    const destinationMap: Record<'popstudy' | 'popquiz' | 'popteams' | 'popstack', string> = {
      popstudy: this.isDevelopment
        ? 'https://nx-popstudy.herokuapp.com'
        : 'https://www.popstudy.com',
      popquiz: this.isDevelopment
        ? 'https://superprep-quiz.web.app'
        : 'https://www.popquiz.io',
      popteams: this.isDevelopment
        ? 'https://www.ltclassroom-dev.com'
        : 'https://www.popteams.com',
      popstack: this.isDevelopment
        ? 'https://superprep-stack-git-main-longtail-education.vercel.app?_vercel_share=MnujWaewZp0PvkjoMAfCRcWd9yO6RkIU&destinationUrl=https://superprep-stack-git-main-longtail-education.vercel.app'
        : 'https://www.popstack.it',
    };

    if (item === 'join') {
      if (this.userService.isGuest) {
        this.create();
      } else {
        this.classroom = { status: this.classroom.status ? undefined : 'join' };
        this.classroomChange.emit(this.classroom);
      }
    } else if (item === 'profile') {
      this.profile = !this.profile;
      this.profileChange.emit(this.profile);
    } else if (item === 'logout') {
        if (this.userService.isGuest) { this.confirm() } else { this.signout() }
    } else if (Object.keys(destinationMap).includes(item as string)) {
      if (item === 'popstudy') { return; }
      const destinationUrl = finalRoute
        ? `${destinationMap[item as 'popstudy' | 'popquiz' | 'popteams' | 'popstack']}/${finalRoute}`
        : destinationMap[item as 'popstudy' | 'popquiz' | 'popteams' | 'popstack'];
      this.router.navigate(['/auth_transfer'], { queryParams: { destinationUrl } });
    } else if (item && ['home', 'library', 'performance', 'brushup', 'resources'].includes(item)) {
      this.activeTab = item;
      this.activeTabChange.emit(this.activeTab);
    } else if (item === 'feedback') {
      this.router.navigate(['/feedback'])
    }
  }

  isActive(item: HeaderActionType) {
    if (item === 'join') {
      return this.isJoinActive && !this.profile
    } else if (item === 'profile') {
      return this.profile && !this.isJoinActive
    } else return item === this.activeTab && !(this.isJoinActive || this.profile)
  }

  title(id: string) {
    switch (id) {
      case 'home': return 'Home'
      case 'library': return 'Explore'
      case 'performance': return 'Performance'
      case 'brushup': return 'Brushup'
      case 'join': return 'Classroom Join Code'
      default: return id.toTitleCase()
    }
  }

  ngAfterViewInit() {
    if (!this.root) { this.render() }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.shouldRender(changes)) {
      this.render()
    }
  }

  private shouldRender(changes: SimpleChanges): boolean {
    return true
  }

  override onResize(event: Event): void {
    super.onResize(event)
    this.render()
  }

  handleActiveTabChange = (tab: string) => {
    this.onSelect(tab as HeaderActionType)
  }

  private render() {
    try {
      if (!this.reactRef) { return }
      this.root ??= createRoot(this.reactRef.nativeElement)

      const props = {
        profileName: this.profileName,
        photoURL: this.photoURL,
        isContentManager: this.isContentManager,
        activeTab: this.activeTab,
        onActiveTabChange: this.handleActiveTabChange,
      }

      const navbar = PopStudyNavBar(props)
      this.root.render(
        <React.Fragment>
          <SafeReactWrapper>{navbar}</SafeReactWrapper>
        </React.Fragment>
      )
    } catch (error) {
      log.error('Error rendering React navbar:', error)
    }
  }

  override ngOnInit(): void {
    super.ngOnInit()
    this.userService.getInfo().pipe(takeUntil(this.onDestroy)).subscribe(info => {if (info) {
      this.profileName = info.profileName
      this.photoURL = this.userService.activeUser?.photoURL
      this.render()
    }})
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy()
    this.root?.unmount()
  }

  readonly userService = inject(UserService)
  readonly studyService = inject(StudyService)
  readonly modalService = inject(BsModalService)

  constructor() {
    super()
    super.rootClassName = 'headerlte-root-class-name'
  }
}
